import lodashSet from 'lodash/fp/set';
import { LOGGING_TIMESTAMP_FORMAT_LABELS } from '#components/patterns/config-overrides/labels/ConfigOverrideLabels.ts';
import type { LocalAllowRule } from '#dn-types/config-override.ts';
import type { ConfigOverride } from '#dn-types/host.ts';
import { configOverridesDefaultValues } from '#schemas/config-override/config-overrides-default-values.ts';
import { configOverridesSchema } from '#schemas/config-override/config-overrides-schema.ts';
import type { ConfigOverridesFormData, ConfigOverridesTransformedFormData } from '../../host-form-types.ts';

const defaultTransformedValues = configOverridesSchema.parse(configOverridesDefaultValues);

/**
 * Convert overrides that we get from the API server to values that our form can understand,
 * including default values for all fields.
 */
export function mapOverridesToForm(
  overrides: readonly ConfigOverride[],
  defaultValues = configOverridesDefaultValues
): ConfigOverridesFormData {
  return overrides.reduce<ConfigOverridesFormData>((acc, o) => {
    let savedValue: Array<{ cidr: string; allow: boolean }> | LocalAllowRule[] | string | number;
    switch (o.key) {
      // TODO: consider changing the type of `allow` from boolean to "Allow" | "Deny", which is what the form actually uses
      case 'lighthouse.remote_allow_list':
        savedValue = Object.entries(o.value).map(([cidr, allow]) => ({ cidr, allow }));
        break;
      case 'lighthouse.local_allow_list':
        savedValue = Object.entries(o.value)
          .filter(([key]) => key !== 'interfaces')
          .map(([cidr, allow]) => ({ cidr, allow })) as LocalAllowRule[];
        if ('interfaces' in o.value) {
          savedValue = savedValue.concat(
            Object.entries(o.value.interfaces).map(([iface, allow]) => ({ interface: iface, allow }))
          );
        }
        break;
      case 'lighthouse.serve_dns':
      case 'stats.lighthouse_metrics':
      case 'stats.message_metrics':
        savedValue = o.value ? 'On' : 'Off';
        break;
      case 'listen.udp_offloads':
      case 'tun.disabled':
      case 'tun.use_offloads':
        savedValue = o.value ? 'True' : 'False';
        break;
      case 'logging.timestamp_format':
        savedValue = LOGGING_TIMESTAMP_FORMAT_LABELS[o.value];
        break;
      case 'preferred_ranges':
      case 'lighthouse.advertise_addrs':
      case 'definednet.dns.resolver_addrs':
      case 'definednet.dns.match_domains':
      case 'definednet.dns.search_domains':
        savedValue = o.value.join(', ');
        break;
      case 'routines':
      case 'lighthouse.interval':
      case 'lighthouse.dns.host':
      case 'lighthouse.dns.port':
      case 'listen.batch':
      case 'listen.read_buffer':
      case 'listen.write_buffer':
      case 'listen.host':
      case 'logging.format':
      case 'logging.level':
      case 'stats.host':
      case 'stats.interval':
      case 'punchy.delay':
      case 'punchy.respond_delay':
      case 'stats.listen':
      case 'stats.namespace':
      case 'stats.path':
      case 'stats.prefix':
      case 'stats.subsystem':
      case 'stats.type':
      case 'static_map.network':
      case 'tun.dev':
      case 'tun.mtu':
      case 'tun.tx_queue':
        savedValue = o.value;
        break;
      default:
        // Keys the api supports but we have no field for; the form must not carry them.
        return acc;
    }

    acc = lodashSet(o.key, savedValue)(acc);
    return acc;
  }, defaultValues);
}

/**
 * Converts a set of overrides to transformed form data, using transformed default values as a base
 */
export function mapOverridesToTransformedForm(
  overrides: readonly ConfigOverride[],
  defaultValues = defaultTransformedValues
): ConfigOverridesTransformedFormData {
  return overrides.reduce<ConfigOverridesTransformedFormData>((acc, o) => {
    let savedValue: Array<{ cidr: string; allow: boolean }> | LocalAllowRule[] | string | number;
    switch (o.key) {
      // TODO: consider changing the type of `allow` from boolean to "Allow" | "Deny", which is what the form actually uses
      case 'lighthouse.remote_allow_list':
        savedValue = Object.entries(o.value).map(([cidr, allow]) => ({ cidr, allow }));
        break;
      case 'lighthouse.local_allow_list':
        savedValue = Object.entries(o.value)
          .filter(([key]) => key !== 'interfaces')
          .map(([cidr, allow]) => ({ cidr, allow })) as LocalAllowRule[];
        if ('interfaces' in o.value) {
          savedValue = savedValue.concat(
            Object.entries(o.value.interfaces).map(([iface, allow]) => ({ interface: iface, allow }))
          );
        }
        break;
      case 'lighthouse.serve_dns':
      case 'stats.lighthouse_metrics':
      case 'stats.message_metrics':
        savedValue = o.value ? 'On' : 'Off';
        break;
      case 'listen.udp_offloads':
      case 'tun.disabled':
      case 'tun.use_offloads':
        savedValue = o.value ? 'True' : 'False';
        break;
      case 'logging.timestamp_format':
        savedValue = LOGGING_TIMESTAMP_FORMAT_LABELS[o.value];
        break;
      case 'preferred_ranges':
      case 'lighthouse.advertise_addrs':
      case 'definednet.dns.resolver_addrs':
      case 'definednet.dns.match_domains':
      case 'definednet.dns.search_domains':
        savedValue = o.value.join(', ');
        break;
      case 'routines':
      case 'lighthouse.interval':
      case 'lighthouse.dns.host':
      case 'lighthouse.dns.port':
      case 'listen.batch':
      case 'listen.read_buffer':
      case 'listen.write_buffer':
      case 'listen.host':
      case 'logging.format':
      case 'logging.level':
      case 'stats.host':
      case 'stats.interval':
      case 'punchy.delay':
      case 'punchy.respond_delay':
      case 'stats.listen':
      case 'stats.namespace':
      case 'stats.path':
      case 'stats.prefix':
      case 'stats.subsystem':
      case 'stats.type':
      case 'static_map.network':
      case 'tun.dev':
      case 'tun.mtu':
      case 'tun.tx_queue':
        savedValue = o.value;
        break;
      default:
        // Keys the api supports but we have no field for; the form must not carry them.
        return acc;
    }

    acc = lodashSet(o.key, savedValue)(acc);
    return acc;
  }, defaultValues);
}
